// ANANT SAMACHAR NETWORK — Full Article Page (SEO-optimized with JSON-LD)
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Clock, Eye, Share2, Volume2, VolumeX, Loader2 } from 'lucide-react';
import api from '../../utils/api';

export default function AnantSamacharArticle() {
  const { contentId } = useParams();
  const [n, setN] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('hi-IN');
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    api.get(`/asn/content/${contentId}`)
      .then((r) => setN(r.data?.content))
      .catch(() => toast.error('Article not found'))
      .finally(() => setLoading(false));
  }, [contentId]);

  // Inject JSON-LD SEO schema
  useEffect(() => {
    if (!n) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    const schema = n.content_type === 'VIDEO' && n.youtube_url ? {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: n.title,
      description: (n.body || n.title).slice(0, 500),
      thumbnailUrl: n.media_url ? [n.media_url] : undefined,
      uploadDate: n.created_at,
      contentUrl: n.youtube_url,
      embedUrl: n.youtube_url,
    } : {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: n.title,
      datePublished: n.created_at,
      dateModified: n.updated_at || n.created_at,
      author: { '@type': 'Person', name: n.username || 'ASN Reporter' },
      publisher: {
        '@type': 'NewsMediaOrganization',
        name: 'Anant Samachar Network',
        logo: { '@type': 'ImageObject', url: `${window.location.origin}/favicon.ico` },
      },
      image: n.media_url ? [n.media_url] : undefined,
      articleSection: n.category,
      articleBody: n.body,
      locationCreated: { '@type': 'Place', name: n.city },
    };
    script.innerHTML = JSON.stringify(schema);
    document.head.appendChild(script);
    // Also set page title
    document.title = `${n.title} — ASN`;
    return () => { document.head.removeChild(script); document.title = 'Anant Samachar Network'; };
  }, [n]);

  const speak = () => {
    if (!window.speechSynthesis) return toast.error('TTS not supported');
    if (speaking) { window.speechSynthesis.cancel(); setSpeaking(false); return; }
    const u = new window.SpeechSynthesisUtterance(`${n.title}. ${n.body || ''}`);
    u.lang = lang;
    const voices = window.speechSynthesis.getVoices();
    const v = voices.find((x) => x.lang === lang) || voices.find((x) => x.lang?.startsWith(lang.split('-')[0]));
    if (v) u.voice = v;
    u.rate = 0.95;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(u);
  };

  const share = async () => {
    const url = window.location.href;
    const text = `📰 ${n.title}\n\n${(n.body || '').slice(0, 200)}${(n.body || '').length > 200 ? '…' : ''}\n\n${url}\n\n— ANANT SAMACHAR NETWORK`;
    if (navigator.share) {
      try { await navigator.share({ title: n.title, text, url }); } catch { /* cancel */ }
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!n) return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center gap-3">
      <div className="text-lg">Article not found or expired.</div>
      <Link to="/asn" className="text-blue-300 underline">← Back to ASN</Link>
    </div>
  );

  const ytEmbed = n.youtube_url ? n.youtube_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/') : null;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-blue-950 via-slate-950 to-black text-white" data-testid="asn-article-page">
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-blue-400/60 px-4 py-2 flex items-center justify-between shadow-2xl shadow-black">
        <Link to="/asn" className="text-xs text-slate-300 hover:text-white flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> ASN Feed</Link>
        <button onClick={share} data-testid="article-share" className="text-[11px] px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black flex items-center gap-1"><Share2 className="w-3 h-3" /> Share</button>
      </div>

      <article className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 text-[11px] opacity-80 mb-2 flex-wrap">
          <span className="px-2 py-0.5 rounded bg-blue-500/25 text-blue-300 font-black">{n.category || 'General'}</span>
          <span className="px-2 py-0.5 rounded bg-purple-500/25 text-purple-300 font-black">{n.content_type}</span>
          <MapPin className="w-3 h-3" /> {n.city}
          <Clock className="w-3 h-3 ml-2" /> {new Date(n.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          <Eye className="w-3 h-3 ml-2" /> {(n.views || 0).toLocaleString()} views
        </div>

        <h1 className="text-2xl sm:text-4xl font-black mb-3 leading-tight" style={{ fontFamily: '"Cinzel", Georgia, serif' }}>{n.title}</h1>

        <div className="text-[11px] opacity-70 mb-4 flex items-center gap-2">
          <span>— <b className="text-amber-200">{n.reporter_id || 'ASN Editor'}</b> · @{n.username}</span>
          <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="article-lang" className="ml-auto text-[10px] px-1.5 py-1 rounded bg-slate-800 border border-slate-700">
            <option value="hi-IN">🇮🇳 Hindi</option><option value="en-IN">🇬🇧 English</option>
            <option value="mr-IN">मराठी</option><option value="gu-IN">ગુજરાતી</option>
            <option value="bn-IN">বাংলা</option><option value="ta-IN">தமிழ்</option>
            <option value="te-IN">తెలుగు</option><option value="kn-IN">ಕನ್ನಡ</option>
            <option value="ml-IN">മലയാളം</option><option value="pa-IN">ਪੰਜਾਬੀ</option>
          </select>
          <button onClick={speak} data-testid="article-listen" className={`text-[10px] px-2 py-1 rounded-lg font-black flex items-center gap-1 ${speaking ? 'bg-red-500/25 text-red-300' : 'bg-emerald-500 text-black'}`}>
            {speaking ? <><VolumeX className="w-3 h-3" /> Stop</> : <><Volume2 className="w-3 h-3" /> Listen</>}
          </button>
        </div>

        {n.media_url && <img src={n.media_url} alt={n.title} className="w-full rounded-2xl border-2 border-blue-400/30 mb-4" />}
        {ytEmbed && (
          <div className="aspect-video bg-black rounded-2xl overflow-hidden border-2 border-red-400/30 mb-4">
            <iframe title={n.title} src={ytEmbed} allowFullScreen className="w-full h-full" />
          </div>
        )}

        {n.body && <div className="prose prose-invert max-w-none text-base leading-relaxed whitespace-pre-wrap">{n.body}</div>}

        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-[11px] text-slate-400 leading-relaxed">
          <b className="text-slate-200">Disclaimer:</b> This is user-generated content published on ANANT SAMACHAR NETWORK. Views expressed are of the reporter. ASN does not guarantee accuracy — verify independently.
        </div>
      </article>
    </div>
  );
}
